import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { SubscriptionService, ReportExportService } from "../../api/services";
import {
  UserState,
  US_ReportstandardType,
  US_ReportSubscriptionExport,
  US_ReportStandardLayout,
} from "../../api/models";
import { Router } from "@angular/router";
import { GlobalApp } from "../../tools/global";
import { ConfirmDialogComponent } from "../../dialogs/confirm-dialog/confirm-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { DialogResultType } from "../../dialogs/DialogResult";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { PowerbiReportEmbedComponent, ReportEmbeddedResult, ReportLayoutResult } from "../../elements/powerbi-report-embed/powerbi-report-embed.component";
import { MessageDialogComponent } from "../../dialogs/message-dialog/message-dialog.component";

@Component({
  selector: "report-subscriptions",
  templateUrl: "./report-subscriptions.component.html",
})
export class ReportSubscriptionsComponent implements OnInit, OnDestroy {

  @ViewChild(PowerbiReportEmbedComponent) powerBi : PowerbiReportEmbedComponent;
  showPowerBi: boolean = false;

  userState: UserState = {
    reportOnly: false,
    userManagerOnly: false,
    invoiceManagerOnly: false,
    interactives: [],
    modifiedInteractives: [],
    xmlas: [],
    reportExportState: {},
    isInteractiveUser: false,
    isReportExportUser: false,
    isEmbeddedUser: false,
    hasAiChats: false,
    isValidState: false
  };
  userStateSubscription: Subscription = null;

  public refreshTimer: any = null;
  public loadingData = false;
  public refreshingData: boolean = false;

  public reportStandardTypes: Array<US_ReportstandardType>;
  public reportExports: Array<US_ReportSubscriptionExport>;

  public selectedStandardReport: US_ReportstandardType = undefined;

  public reportSectionId: number = 0;

  constructor(
    private reportExportService: ReportExportService,
    private subscriptionService: SubscriptionService,
    private app: GlobalApp,
    private router: Router,
    private dialog: MatDialog
  ) {
    this.app.getAuthorizationStatus().subscribe((result) => {
      if (!result.isValid) {
        this.app.logOut();
        this.router.navigate(["login"]);
      }
    });
  }

  loadExportState() {
    this.refreshingData = true;
    this.reportExportService.reportExportGetUserReportExportState().pipe(
      finalize(() => this.refreshingData = false)
    ).subscribe((state) => {
      if (!this.loadingData) {
        this.userState.reportExportState.reportExports = state.reportExports;
      }
    });
  }

  canEditReport(): boolean {
    if (this.selectedStandardReport) {
      if (this.selectedStandardReport.canModifyIds.length > 0) {
        return true;
      }
    }

    return false;
  }

  loadUserState() {
    this.loadingData = true;
    this.app.refreshUserState(this.subscriptionService);
  }

  ngOnInit() {
    this.userStateSubscription = this.app.userState.subscribe((userState) => {
      this.userState = userState;

      this.reportStandardTypes = this.userState.reportExportState.reportStandardTypes;
      this.reportExports = this.userState.reportExportState.reportExports;

      const lastStatus = this.app.getLastStatus();

      if (this.userState?.reportExportState?.reportStandardTypes?.length > 0) {
        if (lastStatus.lastEditReportStandardTypeId) {
          this.selectedStandardReport = this.userState.reportExportState.reportStandardTypes.find(x => x.reportStandardTypeId === lastStatus.lastEditReportStandardTypeId);
        } else {
          this.selectedStandardReport = this.userState.reportExportState.reportStandardTypes[0];
        }
      }

      this.loadingData = false;
    });

    // Don't start another refresh until the previous one has completed, or slow responses pile up.
    this.refreshTimer = setInterval(() => {
      if (!this.refreshingData) {
        this.loadExportState();
      }
    }, 20000);

    this.loadUserState();
  }

  ngOnDestroy() {
    if (this.userStateSubscription) {
      this.userStateSubscription.unsubscribe();
    }
    clearInterval(this.refreshTimer);
  }

  reportStandardTypeChanged() {
    this.app.setLastEditStandardType(this.selectedStandardReport.reportStandardTypeId);
  }

  createNewLayout() {
    var newStandardLayout: US_ReportStandardLayout = {
      reportStandardLayoutId: -1,
      reportStandardTypeId: this.selectedStandardReport.reportStandardTypeId,
      subscriptionId: -1,
      userId: -1,
      filterData: undefined,
      footerFileId: undefined,
      headerFileId: undefined,
      backgroundColor: '',
      name: '<New Layout>',
      orderedPages: this.selectedStandardReport.reportPages,
      preFilterId: -1,
      shareMode: 0,
      headerImageUri: undefined,
      footerImageUri: undefined
    }

    var reportSource = this.userState.reportExportState.reportSources.find(x => x.reportSourceId == this.selectedStandardReport.reportSourceId);

    this.powerBi.loadReportState(true, newStandardLayout, this.userState.preFilters, reportSource, this.selectedStandardReport, true, this.userState);
    this.showPowerBi = true;

    return false;
  }

  editLayout(layout: US_ReportStandardLayout) {
    var reportSource = this.userState.reportExportState.reportSources.find(x => x.reportSourceId == this.selectedStandardReport.reportSourceId);

    this.powerBi.loadReportState(true, layout, this.userState.preFilters, reportSource, this.selectedStandardReport, false, this.userState);
    this.showPowerBi = true;

    return false;
  }

  deleteLayout(layout: US_ReportStandardLayout) {
    ConfirmDialogComponent.showDialog(
      this.dialog,
      "Confirm Delete Report Layout?",
      "This will permanately delete this layout, and all exports of this type?"
    ).subscribe((confirmResult) => {
      if (confirmResult.ResultType === DialogResultType.Ok) {
        this.reportExportService.reportExportDeleteReportStandardLayout(layout.reportStandardLayoutId).subscribe((_) => {
          MessageDialogComponent.showDialog(this.dialog, "Success", "Report Layout Deleted.").subscribe((_) => { });
          this.loadUserState();
        });
      }
    });

    return false;
  }

  exportReport(layout: US_ReportStandardLayout) {
    this.reportExportService.reportExportReportExportRequest(layout.reportStandardLayoutId).subscribe((_) => {
      MessageDialogComponent.showDialog(this.dialog, "Success", "Export Generation Submitted.  See exports tab for progress.").subscribe((_) => { });
      //this.loadUserState();
      this.loadExportState();
    });

    return false;
  }

  extendExpiration(report: US_ReportSubscriptionExport) {
    this.reportExportService.reportExportReportExportExtend(report.reportExportId).subscribe((res) => {
      this.loadExportState();
    });

    return false;
  }

  deleteExport(report: US_ReportSubscriptionExport) {
    ConfirmDialogComponent.showDialog(this.dialog, 'Delete Report?', 'Are you sure want to delete this report export?').subscribe((res) => {
      if (res.ResultType == DialogResultType.Ok) {
        this.reportExportService.reportExportReportExportDelete(report.reportExportId).subscribe((_) => {
          MessageDialogComponent.showDialog(this.dialog, "Success", "Report Export Deleted.").subscribe((_) => { });
          this.loadExportState();
        });
      }
    });

    return false;
  }

  closeClicked() {
    this.showPowerBi = false;
    return false;
  }

  saveClicked() {
    this.app.pushLoading("Saving...");

    this.powerBi.getLayoutResults().then((results: ReportLayoutResult) => {
      if (results.reportStandardLayoutId == -1) {
        this.reportExportService.reportExportCreateReportStandardLayout({
          reportStandardLayoutUserId: -1,
          reportStandardLayoutSubscriptionId: results.subscriptionId,
          reportStandardLayoutReportStandardTypeId: results.reportStandardTypeId,
          reportStandardLayoutReportStandardLayoutId: -1,
          reportStandardLayoutPreFilterId: results.preFilterId,
          reportStandardLayoutOrderedPages: results.orderedPages,
          reportStandardLayoutName: results.name,
          reportStandardLayoutFilterData: results.filterData,
          reportStandardLayoutBackgroundColor: results.backgroundColor,
          headerFileUpload: results.headerFile,
          footerFileUpload: results.footerFile,
          clearHeaderFile: results.clearHeader,
          clearFooterFile: results.clearFooter
        }).subscribe((newLayout) => {
          this.reportExportService.reportExportGetUserReportExportState().subscribe((updatedState) => {
            var targetStandardType = updatedState.reportStandardTypes.find(x => x.reportStandardTypeId == this.selectedStandardReport.reportStandardTypeId);
            if (targetStandardType) {
              var targetLayout = targetStandardType.layouts.find(x => x.reportStandardLayoutId == newLayout.reportStandardLayoutId);
              if (targetLayout) {
                this.selectedStandardReport.layouts.push(targetLayout);
              }
            }
            this.app.popLoading();
          });
        })
      } else {
        this.reportExportService.reportExportUpdateReportStandardLayout({
          reportStandardLayoutReportStandardLayoutId: results.reportStandardLayoutId,
          reportStandardLayoutSubscriptionId: results.subscriptionId,
          reportStandardLayoutReportStandardTypeId: results.reportStandardTypeId,
          reportStandardLayoutUserId: -1,
          reportStandardLayoutPreFilterId: results.preFilterId,
          reportStandardLayoutOrderedPages: results.orderedPages,
          reportStandardLayoutName: results.name,
          reportStandardLayoutFilterData: results.filterData,
          reportStandardLayoutBackgroundColor: results.backgroundColor,
          headerFileUpload: results.headerFile,
          footerFileUpload: results.footerFile,
          clearHeaderFile: results.clearHeader,
          clearFooterFile: results.clearFooter,

        }).subscribe((updatedLayout) => {
          this.reportExportService.reportExportGetUserReportExportState().subscribe((updatedState) => {
            var targetStandardType = updatedState.reportStandardTypes.find(x => x.reportStandardTypeId == this.selectedStandardReport.reportStandardTypeId);
            if (targetStandardType) {
              var targetLayout = targetStandardType.layouts.find(x => x.reportStandardLayoutId == updatedLayout.reportStandardLayoutId);
              if (targetLayout) {
                var idx = -1;
                for (var i = 0; i < this.selectedStandardReport.layouts.length; i++) {
                  if (this.selectedStandardReport.layouts[i].reportStandardLayoutId == updatedLayout.reportStandardLayoutId) {
                    idx = i;
                    break;
                  }
                }
                this.selectedStandardReport.layouts[idx] = targetLayout;
              }
            }
            this.app.popLoading();
          });
        })
      }
    });

    this.showPowerBi = false;
    return false;
  }
}
